import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  FogExp2,
  Group,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Points,
  PointsMaterial,
  RingGeometry,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import type { LevelDefinition } from '../config/levelConfig';
import type { RandomManager } from '../core/RandomManager';
import { CameraController } from './CameraController';
import { Environment } from './Environment';
import { createLighting } from './Lighting';
import { WaterSystem } from './WaterSystem';

interface Ripple {
  mesh: Mesh;
  age: number;
}

interface ConfettiBurst {
  points: Points;
  velocities: Float32Array;
  age: number;
}

export class RiverScene {
  readonly scene = new Scene();
  readonly renderer: WebGLRenderer;
  readonly cameraController: CameraController;
  readonly environment: Environment;
  readonly water: WaterSystem;
  readonly worldGroup = new Group();
  readonly effectsGroup = new Group();
  readonly mobile: boolean;
  readonly reducedMotion: boolean;
  private composer: EffectComposer | null = null;
  private trajectory: Points | null = null;
  private ripples: Ripple[] = [];
  private confetti: ConfettiBurst[] = [];
  private storm = false;

  constructor(container: HTMLElement, random: RandomManager, quality: 'low' | 'high' = 'high') {
    this.mobile = matchMedia('(pointer: coarse)').matches || window.innerWidth < 760;
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scene.background = new Color(0x9ddff3);
    this.scene.fog = new FogExp2(0xb8e6ee, 0.0088);
    this.renderer = new WebGLRenderer({
      antialias: !this.mobile && quality === 'high',
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, this.mobile ? 1.4 : 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = quality === 'high';
    this.renderer.shadowMap.autoUpdate = true;
    this.renderer.domElement.id = 'game-canvas';
    this.renderer.domElement.setAttribute('aria-label', '三维卡通河流救援场景');
    container.prepend(this.renderer.domElement);

    this.cameraController = new CameraController(window.innerWidth / window.innerHeight);
    this.water = new WaterSystem(this.mobile || quality === 'low');
    this.environment = new Environment(random);
    this.worldGroup.add(this.water.mesh, this.environment.group);
    this.scene.add(this.worldGroup, this.effectsGroup, createLighting());

    if (!this.mobile && quality === 'high') {
      this.composer = new EffectComposer(this.renderer);
      this.composer.addPass(new RenderPass(this.scene, this.camera));
      const bloom = new UnrealBloomPass(
        new Vector3(window.innerWidth, window.innerHeight) as never,
        0.18,
        0.45,
        0.88,
      );
      bloom.threshold = 0.82;
      bloom.strength = 0.2;
      bloom.radius = 0.3;
      this.composer.addPass(bloom);
      this.composer.addPass(new OutputPass());
    }
  }

  get camera(): PerspectiveCamera {
    return this.cameraController.camera;
  }

  configure(level: LevelDefinition): void {
    this.storm = level.storm;
    this.environment.configure(level);
    this.scene.background = new Color(level.storm ? 0x697f91 : 0x9ddff3);
    this.scene.fog = new FogExp2(level.storm ? 0x718797 : 0xb8e6ee, level.storm ? 0.014 : 0.0088);
  }

  update(time: number, delta: number, riverSpeed: number): void {
    this.water.update(time, riverSpeed);
    this.environment.update(time, delta, riverSpeed, this.storm);
    this.cameraController.update(delta, this.reducedMotion);
    this.updateEffects(delta);
  }

  render(): void {
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }

  showTrajectory(origin: Vector3, target: Vector3, power: number, wind: number, visible = true): void {
    if (!visible) {
      if (this.trajectory) this.trajectory.visible = false;
      return;
    }
    if (!this.trajectory) {
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(22 * 3), 3));
      const material = new PointsMaterial({
        color: 0xfff7e8,
        size: 0.22,
        transparent: true,
        opacity: 0.84,
        depthWrite: false,
      });
      this.trajectory = new Points(geometry, material);
      this.effectsGroup.add(this.trajectory);
    }
    this.trajectory.visible = true;
    const positions = this.trajectory.geometry.getAttribute('position') as BufferAttribute;
    const flightTime = 1.72;
    const velocity = target.clone().sub(origin);
    velocity.x = velocity.x / flightTime + wind * 1.2;
    velocity.z /= flightTime;
    velocity.y = (target.y - origin.y + 0.5 * 9.82 * flightTime * flightTime) / flightTime;
    const strength = 0.68 + power * 0.56;
    velocity.set(velocity.x * strength, velocity.y * (0.82 + power * 0.25), velocity.z * strength);
    for (let i = 0; i < positions.count; i += 1) {
      const t = (i / (positions.count - 1)) * 1.9;
      positions.setXYZ(
        i,
        origin.x + velocity.x * t + wind * t * t * 0.12,
        origin.y + velocity.y * t - 4.91 * t * t,
        origin.z + velocity.z * t,
      );
    }
    positions.needsUpdate = true;
  }

  hideTrajectory(): void {
    if (this.trajectory) this.trajectory.visible = false;
  }

  splash(position: Vector3): void {
    for (let i = 0; i < 3; i += 1) {
      const mesh = new Mesh(
        new RingGeometry(0.7 + i * 0.18, 0.82 + i * 0.2, 30),
        new MeshBasicMaterial({
          color: 0xd7f8ff,
          transparent: true,
          opacity: 0.7 - i * 0.12,
          depthWrite: false,
          side: 2,
        }),
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.copy(position);
      mesh.position.y = 0.52 + i * 0.01;
      mesh.scale.setScalar(0.2);
      this.effectsGroup.add(mesh);
      this.ripples.push({ mesh, age: -i * 0.08 });
    }
  }

  celebrate(position: Vector3): void {
    const count = this.mobile ? 70 : 150;
    const geometry = new BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);
    const palette = [new Color(0xff6b4a), new Color(0xffc83d), new Color(0x5bd675), new Color(0x5d63d8)];
    for (let i = 0; i < count; i += 1) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y + 4;
      positions[i * 3 + 2] = position.z;
      const color = palette[i % palette.length];
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
      velocities[i * 3] = (Math.random() - 0.5) * 8;
      velocities[i * 3 + 1] = Math.random() * 8 + 4;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('color', new BufferAttribute(colors, 3));
    const points = new Points(
      geometry,
      new PointsMaterial({
        size: 0.3,
        vertexColors: true,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    );
    this.effectsGroup.add(points);
    this.confetti.push({ points, velocities, age: 0 });
  }

  private updateEffects(delta: number): void {
    this.ripples = this.ripples.filter((ripple) => {
      ripple.age += delta;
      if (ripple.age < 0) return true;
      ripple.mesh.scale.setScalar(0.2 + ripple.age * 4.2);
      (ripple.mesh.material as MeshBasicMaterial).opacity = Math.max(0, 0.68 - ripple.age * 0.7);
      if (ripple.age > 1) {
        this.effectsGroup.remove(ripple.mesh);
        ripple.mesh.geometry.dispose();
        (ripple.mesh.material as MeshBasicMaterial).dispose();
        return false;
      }
      return true;
    });

    this.confetti = this.confetti.filter((burst) => {
      burst.age += delta;
      const positions = burst.points.geometry.getAttribute('position') as BufferAttribute;
      for (let i = 0; i < positions.count; i += 1) {
        burst.velocities[i * 3 + 1] -= delta * 8;
        positions.setXYZ(
          i,
          positions.getX(i) + burst.velocities[i * 3] * delta,
          positions.getY(i) + burst.velocities[i * 3 + 1] * delta,
          positions.getZ(i) + burst.velocities[i * 3 + 2] * delta,
        );
      }
      positions.needsUpdate = true;
      (burst.points.material as PointsMaterial).opacity = Math.max(0, 1 - burst.age / 3.2);
      if (burst.age > 3.2) {
        this.effectsGroup.remove(burst.points);
        burst.points.geometry.dispose();
        (burst.points.material as PointsMaterial).dispose();
        return false;
      }
      return true;
    });
  }

  resize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.cameraController.resize(width, height);
    this.water.resize(width, height);
    this.renderer.setSize(width, height);
    this.composer?.setSize(width, height);
  }

  dispose(): void {
    this.environment.dispose();
    this.water.dispose();
    this.composer?.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
